import { BuildClass } from '../../../universal';
import { React, _ } from '../../lib';
import { validateTime } from '../component-main';
import { stubFocusable, stubInput, stubTextarea } from './stubs';
export const TextboxComponent = (props, ref) => {
    const refInput = React.useRef(stubFocusable(stubInput));
    React.useImperativeHandle(ref, () => refInput.current);
    return (React.createElement(TextboxGeneric, { ref: refInput, value: String(props.value ?? ''), onUpdate: props.onUpdate, border: props.border, fixOnBlur: props.fixOnBlur ?? _.identity, serialize: props.serialize ?? String, deserialize: props.deserialize ?? String, selectOnClick: props.selectOnClick ?? false, ..._.omit(props, [
            'value',
            'onUpdate',
            'border',
            'fixOnBlur',
            'serialize',
            'deserialize',
            'selectOnClick',
            'ref',
        ]) }));
};
export const Textbox = React.forwardRef(TextboxComponent);
export const TextboxNumeric = (props) => (React.createElement(TextboxGeneric, { value: props.value, onUpdate: props.onUpdate, border: props.border, fixOnBlur: props.fixOnBlur ?? (v => v.toFixed(2)), serialize: props.serialize ?? (v => String(v)), deserialize: props.deserialize ?? (s => +s), selectOnClick: props.selectOnClick ?? false, ..._.omit(props, [
        'value',
        'onUpdate',
        'border',
        'fixOnBlur',
        'serialize',
        'deserialize',
        'selectOnClick',
        'ref',
    ]) }));
export const TextboxTime = React.forwardRef((props, ref) => (React.createElement(TextboxGeneric, { ref: ref, value: props.value, onUpdate: props.onUpdate, border: props.border, fixOnBlur: props.fixOnBlur ?? validateTime, serialize: props.serialize ?? _.identity, deserialize: props.deserialize ?? _.identity, placeholder: props.placeholder ?? 'hh:mm', selectOnClick: props.selectOnClick ?? false, ..._.omit(props, [
        'value',
        'onUpdate',
        'border',
        'fixOnBlur',
        'serialize',
        'deserialize',
        'placeholder',
        'selectOnClick',
        'ref',
    ]) })));
const TextboxGenericComponent = (props, ref) => {
    const serialize = props.serialize ?? _.identity;
    const deserialize = props.deserialize ?? _.identity;
    const refInput = React.useRef(stubInput);
    const onChange = React.useCallback((ev) => {
        const nVal = deserialize(ev.target.value);
        const origValue = deserialize(serialize(props.value));
        if (nVal != origValue) {
            props.onUpdate(nVal);
        }
    }, [props.value, props.onUpdate, deserialize, serialize]);
    const onBlur = React.useCallback((ev) => {
        props.onBlur?.(ev);
        const nVal = deserialize(props.fixOnBlur(deserialize(ev.target.value)));
        if (props.value != nVal) {
            props.onUpdate(nVal);
        }
    }, [props.onBlur, props.fixOnBlur, deserialize, props.onUpdate, props.value]);
    const onClick = React.useCallback((e) => {
        if (props.selectOnClick) {
            refInput.current.select();
        }
        props.onClick?.(e);
    }, [props.onClick, props.selectOnClick]);
    React.useImperativeHandle(ref, () => ({
        focus: () => {
            refInput.current.focus();
        },
        select: () => {
            refInput.current.select();
        },
        getElement: () => refInput.current,
    }));
    return (React.createElement("input", { ref: refInput, type: props.type ?? 'text', className: BuildClass({
            'ui5 ui5-textbox': true,
            [props.className ?? '']: true,
            underlined: props.border == 'underlined' || props.border == null,
            boxed: props.border == 'boxed',
            borderless: props.border == 'borderless',
        }), value: serialize(props.value), onChange: onChange, onBlur: onBlur, onClick: onClick, ..._.omit(props, [
            'value',
            'onUpdate',
            'border',
            'fixOnBlur',
            'serialize',
            'deserialize',
            'selectOnClick',
            'ref',
            'type',
            'className',
            'onChange',
            'onBlur',
            'onClick',
        ]) }));
};
export const TextboxGeneric = React.forwardRef(TextboxGenericComponent);
const TextareaComponent = (props, ref) => {
    const serialize = props.serialize ?? (x => x);
    const deserialize = props.deserialize ?? (x => x);
    const fixOnBlur = props.fixOnBlur ?? ((x) => x);
    const refInput = React.useRef(stubTextarea);
    const onChange = React.useCallback((ev) => {
        const nVal = deserialize(ev.target.value);
        if (nVal != props.value) {
            props.onUpdate(nVal);
        }
    }, [props.value, props.onUpdate, deserialize]);
    React.useEffect(() => {
        if (props.autoExpand && refInput.current) {
            refInput.current.style.height = 'auto';
            refInput.current.style.height = `${refInput.current.scrollHeight}px`;
        }
    }, [props.autoExpand, props.value]);
    const onBlur = React.useCallback((ev) => {
        props.onBlur?.(ev);
        const nVal = deserialize(fixOnBlur(deserialize(ev.target.value)));
        if (props.value != nVal) {
            props.onUpdate(nVal);
        }
    }, [props.onBlur, props.value, props.onUpdate, deserialize, fixOnBlur]);
    const onClick = React.useCallback((e) => {
        if (props.selectOnClick) {
            refInput.current.select();
        }
        props.onClick?.(e);
    }, [props.onClick, props.selectOnClick]);
    React.useImperativeHandle(ref, () => refInput.current);
    return (React.createElement("textarea", { ref: refInput, className: BuildClass({
            'ui5 ui5-textarea': !(props.removeBaseClass ?? false),
            [props.className ?? '']: true,
        }), value: serialize(props.value), onChange: onChange, onBlur: onBlur, onClick: onClick, ..._.omit(props, [
            'value',
            'onUpdate',
            'fixOnBlur',
            'serialize',
            'deserialize',
            'ref',
            'className',
            'onChange',
            'onBlur',
            'onClick',
            'selectOnClick',
            'removeBaseClass',
        ]) }));
};
export const Textarea = React.forwardRef(TextareaComponent);
export const SearchBox = React.forwardRef((props, ref) => (React.createElement(Textbox, { ref: ref, type: "search", className: BuildClass(props.className), placeholder: props.placeholder ?? 'Search...', autoFocus: props.autoFocus ?? false, title: props.title, value: props.value, style: props.style, onUpdate: props.onUpdate, onKeyDown: e => {
        if (props.onArrowDelta) {
            switch (e.which) {
                case 40:
                    e.preventDefault();
                    props.onArrowDelta(+1, e.shiftKey);
                    break;
                case 38:
                    e.preventDefault();
                    props.onArrowDelta(-1, e.shiftKey);
                    break;
                case 34:
                    e.preventDefault();
                    props.onArrowDelta(+12, e.shiftKey);
                    break;
                case 33:
                    e.preventDefault();
                    props.onArrowDelta(-12, e.shiftKey);
                    break;
                case 13:
                    props.onEnter?.();
                    break;
            }
        }
        if (e.which == 13) {
            e.preventDefault();
            props.onEnter?.();
        }
    } })));
//# sourceMappingURL=textbox.js.map